import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useSearchParams } from "react-router-dom";

import { checkoutRequest } from "../api/checkout";
import { useCartIdContext } from "../providers";

interface Confirmation {
  orderId: string;
  status: "paid" | "pending" | "expired";
  amount: number;
  currency: string;
  testMode: boolean;
  orderName?: string;
  orderRecorded?: boolean;
}

export function CheckoutReturn() {
  const [parameters] = useSearchParams();
  const sessionId = parameters.get("session_id") || "";
  const currentCartId = useCartIdContext();
  const [cartId] = useState(() => {
    try { return sessionStorage.getItem(`stripeCart:${sessionId}`) || currentCartId; } catch { return currentCartId; }
  });
  const [result, setResult] = useState<Confirmation>();
  const [error, setError] = useState("");
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    let attempts = 0;
    const confirm = async () => {
      try {
        const response = await checkoutRequest<Confirmation>("status", {
          cartId,
          sessionId,
        });
        if (cancelled) return;
        setResult(response);
        if (response.status === "paid") {
          window.dispatchEvent(new CustomEvent("lucky-checkout-paid", { detail: cartId }));
        }
        if ((response.status === "pending" || (response.status === "paid" && !response.orderRecorded)) && ++attempts < 15)
          timer = setTimeout(confirm, 2000);
      } catch {
        if (!cancelled)
          setError(
            "We could not verify the payment yet. Please do not pay again. Contact us if this continues."
          );
      }
    };
    void confirm();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [cartId, sessionId]);
  return (
    <main
      style={{
        width: "100%",
        maxWidth: "680px",
        padding: "3rem 1.5rem",
        margin: "auto",
        textAlign: "left",
      }}
    >
      <Helmet>
        <title>Payment confirmation | Lucky Kids</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <h1>
        {error
          ? "Payment confirmation pending"
          : result?.status === "paid"
          ? "Payment confirmed"
          : result?.status === "expired"
          ? "Checkout expired"
          : "Checking your payment…"}
      </h1>
      <div role="status" aria-live="polite">
        {error ? (
          <p>{error}</p>
        ) : result?.status === "paid" ? (
          <>
            <p>
              {result.testMode
                ? "This was a test payment. No real order will be dispatched."
                : "Your payment has been confirmed."}
            </p>
            <p>Reference: {result.orderName || result.orderId}</p>
            <p>Total: AED {(result.amount / 100).toFixed(2)}</p>
            {!result.testMode && !result.orderRecorded && <p>Your payment is safe. We are finishing your order confirmation; please do not pay again.</p>}
          </>
        ) : result?.status === "expired" ? (
          <p>This payment session expired. Your basket is still available.</p>
        ) : (
          <p>
            We are checking the payment status. Please do not submit another
            payment.
          </p>
        )}
      </div>
      <p>
        <a href="mailto:business@luckykidsking.com">Contact Lucky Kids</a>
      </p>
      <Link to={result?.status === "paid" ? "/shoes" : "/cart"}>{result?.status === "paid" ? "Continue shopping" : "Back to basket"}</Link>
    </main>
  );
}
